/**
 * Hook for fetching and managing calendar notes (todo items per day)
 */

import { useMemo } from 'react'
import { trpc } from '@/trpc/client'
import { formatDateForApi, parseDateFromApi } from '@/lib/calendar-utils'
import type { AppointmentType, CalendarNote } from '@/types/calendar'

interface UseCalendarNotesOptions {
	type: AppointmentType
	from: Date
	to: Date
	enabled?: boolean
}

/**
 * Fetch calendar notes for a date range
 */
export function useCalendarNotes({
	type,
	from,
	to,
	enabled = true,
}: UseCalendarNotesOptions) {
	const query = trpc.calendar.notes.list.useQuery(
		{
			from: new Date(formatDateForApi(from)),
			to: new Date(formatDateForApi(to)),
			type,
		},
		{
			enabled,
			staleTime: 30000,
			refetchOnWindowFocus: true,
		},
	)

	const notes = useMemo(() => {
		if (!query.data) return []
		return query.data as CalendarNote[]
	}, [query.data])

	// Group notes by date
	const notesByDate = useMemo(() => {
		const grouped: Record<string, CalendarNote[]> = {}
		for (const note of notes) {
			if (!grouped[note.date]) {
				grouped[note.date] = []
			}
			grouped[note.date].push(note)
		}
		return grouped
	}, [notes])

	return {
		notes,
		notesByDate,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	}
}

/**
 * Get notes for a specific date
 */
export function useNotesForDate(date: Date, type: AppointmentType) {
	const dateStr = formatDateForApi(date)
	const from = parseDateFromApi(dateStr)
	const to = parseDateFromApi(dateStr)

	const { notesByDate, isLoading, refetch } = useCalendarNotes({
		type,
		from,
		to,
	})

	return {
		notes: notesByDate[dateStr] || [],
		isLoading,
		refetch,
	}
}

/**
 * Mutations for calendar notes
 */
export function useNoteMutations() {
	const utils = trpc.useUtils()

	const createMutation = trpc.calendar.notes.create.useMutation({
		onSuccess: () => {
			utils.calendar.notes.list.invalidate()
		},
	})

	const updateMutation = trpc.calendar.notes.update.useMutation({
		onSuccess: () => {
			utils.calendar.notes.list.invalidate()
		},
	})

	const toggleMutation = trpc.calendar.notes.toggle.useMutation({
		onSuccess: () => {
			utils.calendar.notes.list.invalidate()
		},
	})

	const deleteMutation = trpc.calendar.notes.delete.useMutation({
		onSuccess: () => {
			utils.calendar.notes.list.invalidate()
		},
	})

	const reorderMutation = trpc.calendar.notes.reorder.useMutation({
		onSuccess: () => {
			utils.calendar.notes.list.invalidate()
		},
	})

	return {
		create: createMutation.mutateAsync,
		update: updateMutation.mutateAsync,
		toggle: toggleMutation.mutateAsync,
		delete: deleteMutation.mutateAsync,
		reorder: reorderMutation.mutateAsync,
		isCreating: createMutation.isPending,
		isUpdating: updateMutation.isPending,
		isToggling: toggleMutation.isPending,
		isDeleting: deleteMutation.isPending,
	}
}
